/**
 * ImageLibrary.ts - Image asset management for Stylr
 * 
 * CHANGE HISTORY:
 * - 251102_1246 danielbeer: Initial creation for universal design editor
 * 
 * Manages all image assets used throughout OpCentral:
 * - Upload and storage
 * - Organization (folders, tags)
 * - Usage tracking
 * - Variant management
 * - Like-image updates
 */

import { nanoid } from 'nanoid';

export interface ImageAsset {
  id: string;
  name: string;
  type: 'png' | 'jpg' | 'jpeg' | 'gif' | 'webp' | 'svg';
  url: string;
  thumbnail?: string;
  folder?: string;
  tags: string[];
  metadata: {
    width: number;
    height: number;
    size: number;
    format: string;
    author?: string;
    license?: string;
    description?: string;
  };
  usage: Array<{
    objectType: string;
    objectId: string;
    context: string;
    location: string; // Where in the app
  }>;
  variants?: Array<{
    id: string;
    name: string;
    url: string;
    modifications: Record<string, any>;
  }>;
  createdAt: number;
  updatedAt: number;
}

export interface ImageUploadOptions {
  file: File;
  folder?: string;
  tags?: string[];
  metadata?: Partial<ImageAsset['metadata']>;
}

export interface ImageUpdateOptions {
  updateMode: 'all' | 'variant' | 'context';
  contextId?: string;
  variantName?: string;
}

export class ImageLibrary {
  private images: Map<string, ImageAsset>;
  private folders: Map<string, string[]>; // folderId -> imageIds
  private tagIndex: Map<string, Set<string>>; // tag -> imageIds
  private usageIndex: Map<string, Set<string>>; // objectId -> imageIds
  private actr: any;
  private storage: any; // Aggregatr storage
  private listeners: Set<Function>;

  constructor(options: { actr?: any; storage?: any } = {}) {
    this.images = new Map();
    this.folders = new Map();
    this.tagIndex = new Map();
    this.usageIndex = new Map();
    this.actr = options.actr;
    this.storage = options.storage;
    this.listeners = new Set();

    // Load from storage
    this.loadFromStorage();
  }

  /**
   * Upload a new image to the library
   */
  async upload(options: ImageUploadOptions): Promise<ImageAsset> {
    const { file, folder, tags = [], metadata = {} } = options;

    // Validate file type
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!validTypes.includes(file.type)) {
      throw new Error(`Invalid file type: ${file.type}. Supported: PNG, JPG, GIF, WebP, SVG`);
    }

    // Read file data
    const dataUrl = await this.readFileAsDataUrl(file);
    const dimensions = await this.getImageDimensions(dataUrl);

    // Create image asset
    const image: ImageAsset = {
      id: nanoid(),
      name: file.name,
      type: this.getImageType(file.type),
      url: dataUrl,
      folder,
      tags,
      metadata: {
        width: dimensions.width,
        height: dimensions.height,
        size: file.size,
        format: file.type,
        ...metadata
      },
      usage: [],
      createdAt: Date.now(),
      updatedAt: Date.now()
    };

    // Generate thumbnail for non-SVG images
    if (image.type !== 'svg') {
      image.thumbnail = await this.generateThumbnail(dataUrl, 200, 200);
    }

    // Store image
    this.images.set(image.id, image);

    // Update indexes
    if (folder) {
      if (!this.folders.has(folder)) {
        this.folders.set(folder, []);
      }
      this.folders.get(folder)!.push(image.id);
    }

    tags.forEach(tag => {
      if (!this.tagIndex.has(tag)) {
        this.tagIndex.set(tag, new Set());
      }
      this.tagIndex.get(tag)!.add(image.id);
    });

    // Save to storage
    await this.saveToStorage();

    // Notify listeners
    this.notifyListeners('imageUploaded', image);

    // Log via Actr
    if (this.actr) {
      await this.actr.execute('stylr:image-uploaded', { imageId: image.id });
    }

    return image;
  }

  /**
   * Get image by ID
   */
  getImage(id: string): ImageAsset | undefined {
    return this.images.get(id);
  }

  /**
   * Get all images
   */
  getAllImages(): ImageAsset[] {
    return Array.from(this.images.values());
  }

  /**
   * Get images by folder
   */
  getImagesByFolder(folder: string): ImageAsset[] {
    const imageIds = this.folders.get(folder) || [];
    return imageIds.map(id => this.images.get(id)!).filter(Boolean);
  }

  /**
   * Get images by tag
   */
  getImagesByTag(tag: string): ImageAsset[] {
    const imageIds = this.tagIndex.get(tag) || new Set();
    return Array.from(imageIds).map(id => this.images.get(id)!).filter(Boolean);
  }

  /**
   * Search images
   */
  searchImages(query: string): ImageAsset[] {
    const lowerQuery = query.toLowerCase();
    return this.getAllImages().filter(image => 
      image.name.toLowerCase().includes(lowerQuery) ||
      image.tags.some(tag => tag.toLowerCase().includes(lowerQuery)) ||
      image.metadata.description?.toLowerCase().includes(lowerQuery)
    );
  }

  /**
   * Track image usage
   */
  trackUsage(imageId: string, objectType: string, objectId: string, context: string, location: string): void {
    const image = this.images.get(imageId);
    if (!image) return;

    // Check if already tracked
    const existing = image.usage.find(u => 
      u.objectType === objectType && u.objectId === objectId && u.context === context
    );

    if (!existing) {
      image.usage.push({ objectType, objectId, context, location });
      image.updatedAt = Date.now();

      // Update usage index
      if (!this.usageIndex.has(objectId)) {
        this.usageIndex.set(objectId, new Set());
      }
      this.usageIndex.get(objectId)!.add(imageId);

      this.saveToStorage();
      this.notifyListeners('usageTracked', { imageId, objectId });
    }
  }

  /**
   * Remove usage tracking
   */
  removeUsage(imageId: string, objectId: string): void {
    const image = this.images.get(imageId);
    if (!image) return;

    image.usage = image.usage.filter(u => u.objectId !== objectId);
    image.updatedAt = Date.now();

    // Update usage index
    const usageSet = this.usageIndex.get(objectId);
    if (usageSet) {
      usageSet.delete(imageId);
      if (usageSet.size === 0) {
        this.usageIndex.delete(objectId);
      }
    }

    this.saveToStorage();
    this.notifyListeners('usageRemoved', { imageId, objectId });
  }

  /**
   * Get images used by an object
   */
  getImagesByObject(objectId: string): ImageAsset[] {
    const imageIds = this.usageIndex.get(objectId) || new Set();
    return Array.from(imageIds).map(id => this.images.get(id)!).filter(Boolean);
  }

  /**
   * Update image with like-image logic
   */
  async updateImage(imageId: string, newData: string, options: ImageUpdateOptions): Promise<ImageAsset | ImageAsset[]> {
    const image = this.images.get(imageId);
    if (!image) {
      throw new Error(`Image not found: ${imageId}`);
    }

    const { updateMode, contextId, variantName } = options;

    switch (updateMode) {
      case 'all':
        // Update all instances
        image.url = newData;
        image.updatedAt = Date.now();
        await this.saveToStorage();
        this.notifyListeners('imageUpdated', { imageId, updateMode: 'all' });
        return image;

      case 'variant':
        // Create a variant
        const variant = {
          id: nanoid(),
          name: variantName || `Variant ${(image.variants?.length || 0) + 1}`,
          url: newData,
          modifications: {} // Store what was changed
        };
        
        if (!image.variants) {
          image.variants = [];
        }
        image.variants.push(variant);
        image.updatedAt = Date.now();
        await this.saveToStorage();
        this.notifyListeners('variantCreated', { imageId, variantId: variant.id });
        return image;

      case 'context':
        // Create context-specific variant
        if (!contextId) {
          throw new Error('contextId required for context update mode');
        }
        
        // Create new image for this context
        const contextImage: ImageAsset = {
          ...image,
          id: nanoid(),
          name: `${image.name} (${contextId})`,
          url: newData,
          usage: image.usage.filter(u => u.objectId === contextId),
          createdAt: Date.now(),
          updatedAt: Date.now()
        };
        
        this.images.set(contextImage.id, contextImage);
        
        // Update usage for this context to point to new image
        this.removeUsage(imageId, contextId);
        
        await this.saveToStorage();
        this.notifyListeners('contextImageCreated', { originalId: imageId, newId: contextImage.id });
        return contextImage;

      default:
        throw new Error(`Invalid update mode: ${updateMode}`);
    }
  }

  /**
   * Delete image
   */
  async deleteImage(imageId: string, force: boolean = false): Promise<void> {
    const image = this.images.get(imageId);
    if (!image) return;

    // Check if image is in use
    if (image.usage.length > 0 && !force) {
      throw new Error(`Image is in use in ${image.usage.length} locations. Use force=true to delete anyway.`);
    }

    // Remove from all indexes
    this.images.delete(imageId);

    if (image.folder) {
      const folderImages = this.folders.get(image.folder);
      if (folderImages) {
        const index = folderImages.indexOf(imageId);
        if (index > -1) {
          folderImages.splice(index, 1);
        }
      }
    }

    image.tags.forEach(tag => {
      this.tagIndex.get(tag)?.delete(imageId);
    });

    image.usage.forEach(u => {
      this.usageIndex.get(u.objectId)?.delete(imageId);
    });

    await this.saveToStorage();
    this.notifyListeners('imageDeleted', { imageId });

    if (this.actr) {
      await this.actr.execute('stylr:image-deleted', { imageId });
    }
  }

  /**
   * Update image metadata
   */
  async updateMetadata(imageId: string, metadata: Partial<ImageAsset['metadata']>): Promise<ImageAsset> {
    const image = this.images.get(imageId);
    if (!image) {
      throw new Error(`Image not found: ${imageId}`);
    }

    image.metadata = { ...image.metadata, ...metadata };
    image.updatedAt = Date.now();

    await this.saveToStorage();
    this.notifyListeners('metadataUpdated', { imageId });

    return image;
  }

  /**
   * Update image tags
   */
  async updateTags(imageId: string, tags: string[]): Promise<ImageAsset> {
    const image = this.images.get(imageId);
    if (!image) {
      throw new Error(`Image not found: ${imageId}`);
    }

    // Remove from old tag indexes
    image.tags.forEach(tag => {
      this.tagIndex.get(tag)?.delete(imageId);
    });

    // Update tags
    image.tags = tags;
    image.updatedAt = Date.now();

    // Add to new tag indexes
    tags.forEach(tag => {
      if (!this.tagIndex.has(tag)) {
        this.tagIndex.set(tag, new Set());
      }
      this.tagIndex.get(tag)!.add(imageId);
    });

    await this.saveToStorage();
    this.notifyListeners('tagsUpdated', { imageId });

    return image;
  }

  /**
   * Move image to folder
   */
  async moveToFolder(imageId: string, folder: string): Promise<ImageAsset> {
    const image = this.images.get(imageId);
    if (!image) {
      throw new Error(`Image not found: ${imageId}`);
    }

    // Remove from old folder
    if (image.folder) {
      const oldFolderImages = this.folders.get(image.folder);
      if (oldFolderImages) {
        const index = oldFolderImages.indexOf(imageId);
        if (index > -1) {
          oldFolderImages.splice(index, 1);
        }
      }
    }

    // Add to new folder
    image.folder = folder;
    if (!this.folders.has(folder)) {
      this.folders.set(folder, []);
    }
    this.folders.get(folder)!.push(imageId);

    image.updatedAt = Date.now();
    await this.saveToStorage();
    this.notifyListeners('imageMoved', { imageId, folder });

    return image;
  }

  /**
   * Get all folders
   */
  getFolders(): string[] {
    return Array.from(this.folders.keys());
  }

  /**
   * Get all tags
   */
  getTags(): string[] {
    return Array.from(this.tagIndex.keys());
  }

  /**
   * Add event listener
   */
  on(callback: Function): void {
    this.listeners.add(callback);
  }

  /**
   * Remove event listener
   */
  off(callback: Function): void {
    this.listeners.delete(callback);
  }

  /**
   * Notify listeners
   */
  private notifyListeners(event: string, data: any): void {
    this.listeners.forEach(listener => {
      try {
        listener(event, data);
      } catch (error) {
        console.error('Error in ImageLibrary listener:', error);
      }
    });
  }

  /**
   * Helper: Read file as data URL
   */
  private readFileAsDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  /**
   * Helper: Get image dimensions
   */
  private getImageDimensions(dataUrl: string): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve({ width: img.width, height: img.height });
      img.onerror = reject;
      img.src = dataUrl;
    });
  }

  /**
   * Helper: Generate thumbnail
   */
  private async generateThumbnail(dataUrl: string, maxWidth: number, maxHeight: number): Promise<string> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }

        // Calculate dimensions
        let width = img.width;
        let height = img.height;
        
        if (width > height) {
          if (width > maxWidth) {
            height *= maxWidth / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width *= maxHeight / height;
            height = maxHeight;
          }
        }

        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);
        
        resolve(canvas.toDataURL('image/jpeg', 0.8));
      };
      img.onerror = reject;
      img.src = dataUrl;
    });
  }

  /**
   * Helper: Get image type from MIME type
   */
  private getImageType(mimeType: string): ImageAsset['type'] {
    const typeMap: Record<string, ImageAsset['type']> = {
      'image/png': 'png',
      'image/jpeg': 'jpg',
      'image/jpg': 'jpg',
      'image/gif': 'gif',
      'image/webp': 'webp',
      'image/svg+xml': 'svg'
    };
    return typeMap[mimeType] || 'png';
  }

  /**
   * Load from storage
   */
  private async loadFromStorage(): Promise<void> {
    if (!this.storage) return;

    try {
      const data = await this.storage.get('stylr:images');
      if (data) {
        // Restore images
        data.images.forEach((img: ImageAsset) => {
          this.images.set(img.id, img);
        });

        // Rebuild indexes
        this.images.forEach(image => {
          if (image.folder) {
            if (!this.folders.has(image.folder)) {
              this.folders.set(image.folder, []);
            }
            this.folders.get(image.folder)!.push(image.id);
          }

          image.tags.forEach(tag => {
            if (!this.tagIndex.has(tag)) {
              this.tagIndex.set(tag, new Set());
            }
            this.tagIndex.get(tag)!.add(image.id);
          });

          image.usage.forEach(u => {
            if (!this.usageIndex.has(u.objectId)) {
              this.usageIndex.set(u.objectId, new Set());
            }
            this.usageIndex.get(u.objectId)!.add(image.id);
          });
        });
      }
    } catch (error) {
      console.error('Error loading images from storage:', error);
    }
  }

  /**
   * Save to storage
   */
  private async saveToStorage(): Promise<void> {
    if (!this.storage) return;

    try {
      await this.storage.set('stylr:images', {
        images: Array.from(this.images.values())
      });
    } catch (error) {
      console.error('Error saving images to storage:', error);
    }
  }
}

/**
 * Singleton instance
 */
let imageLibraryInstance: ImageLibrary | null = null;

export function getImageLibrary(options?: { actr?: any; storage?: any }): ImageLibrary {
  if (!imageLibraryInstance) {
    imageLibraryInstance = new ImageLibrary(options);
  }
  return imageLibraryInstance;
}

export function resetImageLibrary(): void {
  imageLibraryInstance = null;
}
